import { useEffect, useState } from "react";

function formatRemaining(ms: number | null): string {
  if (ms == null) return "--:--";
  const s = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}

type Props = {
  /** Reads the live value; must be stable (wrap in useCallback). */
  getRemaining: () => number | null;
  className?: string;
};

/**
 * Owns its own tick so a running clock does not re-render its parent.
 *
 * The admin dashboard previously held the remaining time in page state and
 * updated it twice a second, which re-rendered the entire tree: both data
 * tables, the challenge picker and every form: just to move one digit. That
 * alone accounted for most of the main-thread time on the page.
 *
 * Ticking faster than the displayed resolution is also wasted work, so the
 * state setter returns the previous string when the formatted value has not
 * changed, which makes React bail out of the render entirely.
 */
export default function Countdown({ getRemaining, className }: Props) {
  const [text, setText] = useState(() => formatRemaining(getRemaining()));

  useEffect(() => {
    const id = window.setInterval(() => {
      setText((prev) => {
        const next = formatRemaining(getRemaining());
        return next === prev ? prev : next;
      });
    }, 500);
    return () => window.clearInterval(id);
  }, [getRemaining]);

  return <span className={className}>{text}</span>;
}
